import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type {
  ApiResponse,
  CancelRecurringClassEnrollmentParams,
  CreateBulkRecurringClassEnrollmentRequest,
  BulkRecurringClassEnrollmentResponse,
  EligibleRecurringEnrollmentMemberResponse,
  RecurringClassEnrollmentResponse,
} from "@membentry/shared-types";
import type { AxiosInstance } from "axios";

import {
  classSeriesKeys,
  invalidateClubClasses,
  invalidateClubClassSeries,
  invalidateMyBookings,
  recurringClassEnrollmentKeys,
  RECURRING_CLASS_ENROLLMENTS_MUTATION_KEY,
  RECURRING_CLASS_ENROLLMENTS_QUERY_KEY,
  invalidateRecurringClassEnrollments,
} from "./useClassHooks";

type ApiClientLikeError = {
  message: string;
  status?: number;
  originalError?: unknown;
  isApiClientError?: true;
};

export {
  recurringClassEnrollmentKeys,
  RECURRING_CLASS_ENROLLMENTS_MUTATION_KEY,
  RECURRING_CLASS_ENROLLMENTS_QUERY_KEY,
};

export function useGetRecurringClassEnrollmentsQuery(
  client: AxiosInstance,
  clubId: number,
  seriesId: number,
  options?: Omit<
    UseQueryOptions<ApiResponse<RecurringClassEnrollmentResponse[]>>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: recurringClassEnrollmentKeys.list(clubId, seriesId),
    queryFn: async () => {
      const { getRecurringClassEnrollments } = await import("@membentry/shared-api");
      return getRecurringClassEnrollments(client, clubId, seriesId);
    },
    enabled: clubId > 0 && seriesId > 0,
    ...options,
  });
}

export function useGetEligibleRecurringEnrollmentMembersQuery(
  client: AxiosInstance,
  clubId: number,
  seriesId: number,
  search?: string,
  options?: Omit<
    UseQueryOptions<ApiResponse<EligibleRecurringEnrollmentMemberResponse[]>>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: [RECURRING_CLASS_ENROLLMENTS_QUERY_KEY, "eligible-members", clubId, seriesId, search],
    queryFn: async () => {
      const { getEligibleRecurringEnrollmentMembers } = await import("@membentry/shared-api");
      return getEligibleRecurringEnrollmentMembers(client, clubId, seriesId, search);
    },
    enabled: clubId > 0 && seriesId > 0,
    ...options,
  });
}

export function useCreateRecurringClassEnrollmentMutation(
  client: AxiosInstance,
  clubId: number,
  seriesId: number,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<BulkRecurringClassEnrollmentResponse>,
      ApiClientLikeError,
      CreateBulkRecurringClassEnrollmentRequest
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, onSettled, ...mutationOptions } = options ?? {};

  return useMutation({
    mutationKey: [RECURRING_CLASS_ENROLLMENTS_MUTATION_KEY, clubId, seriesId, "bulk"],
    mutationFn: async (data: CreateBulkRecurringClassEnrollmentRequest) => {
      const { createBulkRecurringClassEnrollment } = await import("@membentry/shared-api");
      return createBulkRecurringClassEnrollment(client, clubId, seriesId, data);
    },
    onSuccess: async (data, variables, _onMutateResult, context) => {
      invalidateClubClassSeries(queryClient, clubId);
      queryClient.invalidateQueries({
        queryKey: classSeriesKeys.detail(clubId, seriesId),
      });
      invalidateClubClasses(queryClient, clubId);
      invalidateMyBookings(queryClient, clubId);
      invalidateRecurringClassEnrollments(queryClient, clubId, seriesId);
      await onSuccess?.(data, variables, undefined, context);
    },
    onSettled: async (data, error, variables, _onMutateResult, context) => {
      await onSettled?.(data, error, variables, undefined, context);
    },
    ...mutationOptions,
  });
}

export function useCancelRecurringClassEnrollmentMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<RecurringClassEnrollmentResponse>,
      ApiClientLikeError,
      CancelRecurringClassEnrollmentParams
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, onSettled, ...mutationOptions } = options ?? {};

  return useMutation({
    mutationKey: [RECURRING_CLASS_ENROLLMENTS_MUTATION_KEY, "cancel"],
    mutationFn: async (params: CancelRecurringClassEnrollmentParams) => {
      const { cancelRecurringClassEnrollment } = await import("@membentry/shared-api");
      return cancelRecurringClassEnrollment(client, params);
    },
    onSuccess: async (data, variables, _onMutateResult, context) => {
      invalidateClubClassSeries(queryClient, variables.clubId);
      queryClient.invalidateQueries({
        queryKey: classSeriesKeys.detail(variables.clubId, variables.seriesId),
      });
      invalidateClubClasses(queryClient, variables.clubId);
      invalidateMyBookings(queryClient, variables.clubId);
      invalidateRecurringClassEnrollments(
        queryClient,
        variables.clubId,
        variables.seriesId,
      );
      await onSuccess?.(data, variables, undefined, context);
    },
    onSettled: async (data, error, variables, _onMutateResult, context) => {
      await onSettled?.(data, error, variables, undefined, context);
    },
    ...mutationOptions,
  });
}
